import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBan, FaCheckCircle, FaPauseCircle, FaPlayCircle, FaPlus, FaRegFile, FaStamp, FaTrashAlt } from "react-icons/fa";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar, { type U2000ToolbarAction } from "../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { NS, U3125ROLES } from "../../../U3125";
import {
  buildInvoiceSetStatusBody,
  luHandleAppiError,
  u3125CanDeleteInvoice,
  u3125CanModifyInvoice,
  u3125CanVoidInvoice,
  u3125IsWriteOffDocument,
  type U3125InvoiceFormRow,
  type U3125InvoiceStatus,
} from "../../../U3125Service";
import { useU3125SetInvoiceStatusHandlers } from "../../../U3125UseApiHandlers";

interface U3125FactureFormToolBarProps {
  formStateHandlers: U2000FormStateHandlers<U3125InvoiceFormRow> & {
    getData: () => U3125InvoiceFormRow | null;
    getFormState: () => U2000FormStateEnumState;
    addNewInvoice: () => void;
    modifyInvoice: () => void;
    deleteInvoice: () => void;
    afterSaveData: (row: U3125InvoiceFormRow) => void;
  };
  onStatusApplied?: (row: U3125InvoiceFormRow) => void;
  onVoidClick?: () => void;
  onCollectFullBalanceAndClose?: () => void | Promise<void>;
  closePending?: boolean;
}

export const U3125FactureFormToolBar: React.FC<U3125FactureFormToolBarProps> = ({
  formStateHandlers,
  onStatusApplied,
  onVoidClick,
  onCollectFullBalanceAndClose,
  closePending = false,
}) => {
  const formState = formStateHandlers.getFormState();
  const { hasRole } = useU2000AuthContext();
  const { t } = useTranslation();
  const toast = useToast();
  const [statusPending, setStatusPending] = useState(false);

  const row = formStateHandlers.getData();
  const st = row?.status;
  const id = row?.id?.trim() ?? "";
  const cannotModify = !u3125CanModifyInvoice(st);
  const cannotDelete = !u3125CanDeleteInvoice(st);
  const isWriteOff = u3125IsWriteOffDocument(row?.documentNature ?? "STANDARD");
  const canVoid = u3125CanVoidInvoice(row);

  const setInvoiceStatusHandlers = useU3125SetInvoiceStatusHandlers({
    onSuccess: (formRow) => {
      formStateHandlers.afterSaveData(formRow);
      onStatusApplied?.(formRow);
      toast.success(t("msgInvoiceStatusUpdated", { ns: NS }));
    },
    onWarnings: (warnings) => {
      warnings.forEach((w) => toast.warning(w, t("lblWarningsTitle", { ns: NS })));
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const applyStatus = useCallback(
    async (nextStatus: U3125InvoiceStatus) => {
      const current = formStateHandlers.getData();
      if (!current?.id?.trim()) return;
      setStatusPending(true);
      try {
        await setInvoiceStatusHandlers.applyStatus(current.id, buildInvoiceSetStatusBody(current, nextStatus));
      } finally {
        setStatusPending(false);
      }
    },
    [formStateHandlers, setInvoiceStatusHandlers],
  );

  const actions = useMemo(() => {
    const base: U2000ToolbarAction[] = [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        hasRole: hasRole(U3125ROLES.ADD),
        handleClick: () => formStateHandlers.addNewInvoice(),
        disabled: formState === U2000FormStateEnumState.EDIT,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        hasRole: hasRole(U3125ROLES.EDIT),
        handleClick: () => formStateHandlers.modifyInvoice(),
        disabled: formState === U2000FormStateEnumState.EDIT || cannotModify,
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        hasRole: hasRole(U3125ROLES.DELETE),
        handleClick: () => formStateHandlers.deleteInvoice(),
        disabled: formState === U2000FormStateEnumState.EDIT || cannotDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ];

    const showStatusActions =
      Boolean(st && id) &&
      formState === U2000FormStateEnumState.READ &&
      hasRole(U3125ROLES.EDIT) &&
      st !== "CLOSE" &&
      st !== "VOID";

    if (showStatusActions && st === "OPEN") {
      base.push(
        {
          key: "statusOnHold",
          label: t("btnInvoiceStatusOnHold", { ns: NS }),
          hasRole: true,
          handleClick: () => void applyStatus("ON_HOLD"),
          disabled: statusPending,
          icon: FaPauseCircle,
          Component: U2000ActionBtn,
        },
        {
          key: "statusRelease",
          label: t("btnInvoiceStatusRelease", { ns: NS }),
          hasRole: true,
          handleClick: () => void applyStatus("RELEASE"),
          disabled: statusPending,
          icon: FaStamp,
          Component: U2000ActionBtn,
        },
      );
    } else if (showStatusActions && st === "ON_HOLD") {
      base.push({
        key: "reactivate",
        label: t("btnInvoiceReactivate", { ns: NS }),
        hasRole: true,
        handleClick: () => void applyStatus("OPEN"),
        disabled: statusPending,
        icon: FaPlayCircle,
        Component: U2000ActionBtn,
      });
    } else if (showStatusActions && st === "RELEASE" && !isWriteOff) {
      base.push({
        key: "statusClose",
        label: t("btnInvoiceStatusClose", { ns: NS }),
        hasRole: true,
        handleClick: () => void onCollectFullBalanceAndClose?.(),
        disabled: statusPending || closePending,
        icon: FaCheckCircle,
        Component: U2000ActionBtn,
      });
    }

    if (
      formState === U2000FormStateEnumState.READ &&
      hasRole(U3125ROLES.EDIT) &&
      canVoid &&
      onVoidClick
    ) {
      base.push({
        key: "voidInvoice",
        label: t("btnVoidInvoice", { ns: NS }),
        hasRole: true,
        handleClick: () => onVoidClick(),
        disabled: statusPending,
        icon: FaBan,
        Component: U2000ActionBtn,
      });
    }

    return base;
  }, [
    t,
    hasRole,
    formStateHandlers,
    formState,
    cannotModify,
    cannotDelete,
    st,
    id,
    applyStatus,
    statusPending,
    closePending,
    onCollectFullBalanceAndClose,
    isWriteOff,
    row?.documentNature,
    canVoid,
    onVoidClick,
  ]);

  return <U2000ResponsiveToolbar actions={actions} />;
};
